// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Verlauf: gefilterte, nach Datum gruppierte Einträge (portiert aus docs/referenz/trainingslog.html, #tab-history)
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getAllEntries, getExercises, deleteEntry, onSynced, sofort } from "@/lib/data"
import { volume, fmtSet } from "@/lib/sets"
import { dayOf, fmtDate, todayISO } from "@/lib/dates"
import { toast } from "@/lib/toast"
import type { Exercise, WorkoutEntry } from "@/lib/types"
import Igel from "@/components/celebration/creatures/Igel"
import { FeelFace, IconStift, IconPapierkorb, IconSanduhr } from "@/components/icons"
import "@/styles/history.css"

const namen = (exs: Exercise[] | null): ReadonlyMap<string, string> =>
  new Map((exs ?? []).map((x) => [x.id, x.name]))

export default function HistoryPage() {
  const navigate = useNavigate()
  // Aufbau aus dem Sofort-Speicher: beim Tab-Wechsel steht die Seite schon,
  // bevor IndexedDB antwortet — kein Leer-Blitzen (Thors Fund, Runde 16)
  const [entries, setEntries] = useState<WorkoutEntry[]>(() => sofort.entries() ?? [])
  const [exNames, setExNames] = useState<ReadonlyMap<string, string>>(() => namen(sofort.exercises()))
  const [bereit, setBereit] = useState(() => sofort.entries() !== null)
  const [filterDay, setFilterDay] = useState("")
  const [filterEx, setFilterEx] = useState("")
  const [deleteId, setDeleteId] = useState<string | null>(null)

  async function load() {
    const [es, exs] = await Promise.all([getAllEntries(), getExercises()])
    setEntries(es)
    setExNames(namen(exs))
    setBereit(true)
  }
  useEffect(() => {
    void load()
    return onSynced(() => { void load() })
  }, [])

  const exName = (e: WorkoutEntry) => exNames.get(e.exercise) ?? e.exercise
  // ponytail: Badge = day_code direkt vom Eintrag — plan_days-Lookup erst nötig, falls day_code je leer sein kann
  const dayCodes = [...new Set(entries.map((e) => e.day_code))].filter(Boolean).sort()
  const usedNames = [...new Set(entries.map(exName))].sort((a, b) => a.localeCompare(b))

  const filtered = entries.filter(
    (e) => (!filterDay || e.day_code === filterDay) && (!filterEx || exName(e) === filterEx),
  )
  const byDate = new Map<string, WorkoutEntry[]>()
  for (const e of filtered) {
    const d = dayOf(e.date)
    const g = byDate.get(d)
    if (g) g.push(e)
    else byDate.set(d, [e])
  }
  const groups = [...byDate.entries()].sort((a, b) => b[0].localeCompare(a[0]))

  // Kein window.confirm (wird in PWA/eingebettetem Browser verschluckt, §13.6)
  async function onDelete(e: WorkoutEntry) {
    try {
      await deleteEntry(e)
    } catch {
      toast("Löschen ist offline gerade nicht möglich")
    }
    setDeleteId(null)
    await load()
  }

  const monat = todayISO().slice(0, 7)
  const imMonat = entries.filter((e) => dayOf(e.date).startsWith(monat))
  const monatEinheiten = new Set(imMonat.map((e) => dayOf(e.date))).size
  const monatVolumen = imMonat.reduce((s, e) => s + volume(e.sets), 0)

  return (
    <>
      {/* Anker der Seite: die Monats-Bilanz (Skill §13.1/§13.2) */}
      <h2>Diesen Monat</h2>
      <div className="statgrid statgrid--2">
        <div className="stat">
          <span className="stat-lbl">Einheiten</span>
          <b className="num-hero">{monatEinheiten}</b>
        </div>
        <div className="stat">
          <span className="stat-lbl">Volumen</span>
          <b className="num-hero">{monatVolumen.toLocaleString("de-DE")}<i>kg</i></b>
        </div>
      </div>

      <h2>Filter</h2>
      <div className="filterbar">
        <select aria-label="Nach Tag filtern" value={filterDay} onChange={(e) => setFilterDay(e.target.value)}>
          <option value="">Alle Tage</option>
          {dayCodes.map((c) => <option key={c} value={c}>Tag {c}</option>)}
        </select>
        <select aria-label="Nach Übung filtern" value={filterEx} onChange={(e) => setFilterEx(e.target.value)}>
          <option value="">Alle Übungen</option>
          {usedNames.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      {groups.length === 0 ? (
        !bereit ? null : entries.length === 0 ? (
          <div className="empty">
            <div className="critter"><Igel /></div>
            <p><b>Noch kein Eintrag. Dein erster Satz wartet.</b></p>
            <button className="btn-secondary" onClick={() => navigate("/log")}>Training eintragen</button>
          </div>
        ) : (
          <div className="empty"><b>Kein Eintrag passt zum Filter.</b></div>
        )
      ) : (
        groups.map(([date, group]) => {
          const vol = group.reduce((s, e) => s + volume(e.sets), 0)
          return (
            <div className="daygroup" key={date}>
              <div className="dh">
                <b>{fmtDate(date)}</b>
                <span>{group.length} Übungen · {vol.toLocaleString("de-DE")} kg Volumen</span>
              </div>
              <div className="card">
                {group.map((e) => (
                  <div className={e.feel === "schmerz" ? "entry pain enter" : "entry enter"} key={e.id}>
                    <div>
                      <div className="ex">{exName(e)}{e.day_code && <> <span className="badge">{e.day_code}</span></>}</div>
                      <div className="sets">{e.sets.map(fmtSet).join(" · ")}</div>
                      <div className="meta">
                        <span role="img" aria-label={e.feel} title={e.feel}><FeelFace feel={e.feel} size={15} /></span>
                        {e.note ? ` · ${e.note}` : ""}
                        {e.pending ? <> · <IconSanduhr size={13} /> wird gesendet</> : ""}
                      </div>
                    </div>
                    <div className="actions">
                      {deleteId === e.id ? (
                        <>
                          <button className="del-yes" onClick={() => void onDelete(e)}>Löschen</button>
                          <button className="del-no" onClick={() => setDeleteId(null)}>Behalten</button>
                        </>
                      ) : (
                        <>
                          <button aria-label="Eintrag bearbeiten" onClick={() => navigate("/log", { state: { editEntry: e } })}><IconStift size={17} /></button>
                          <button aria-label="Eintrag löschen" onClick={() => setDeleteId(e.id)}><IconPapierkorb size={17} /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })
      )}
    </>
  )
}
